import React, { useState, useEffect } from 'react';
import * as Papa from 'papaparse';
import * as XLSX from 'xlsx';
import axios from 'axios';

const ImportSalaryDetails = ({
    show,
    onHide,
    onImport,
    canImportSalary,
    error,
    setError,
    API_BASE
}) => {
    const [csvData, setCsvData] = useState([]);
    const [importProgress, setImportProgress] = useState(0);
    const [isImporting, setIsImporting] = useState(false);
    const [deductionComponents, setDeductionComponents] = useState([]);
    const [loadingComponents, setLoadingComponents] = useState(false);
    const [selectedComponents, setSelectedComponents] = useState([]);
    const [employees, setEmployees] = useState([]);

    // Fetch distinct deduction components from salary_details
    const fetchDeductionComponents = React.useCallback(async () => {
        try {
            setLoadingComponents(true);
            const token = localStorage.getItem('authToken');

            const response = await axios.get(
                `${API_BASE}imports/get_salary_deduction_components.php`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                setDeductionComponents(response.data.components || []);
            }
        } catch (error) {
            console.error('Error fetching deduction components:', error);
            setError('Failed to load deduction components');
        } finally {
            setLoadingComponents(false);
        }
    }, [API_BASE, setError]);

    // Fetch employees for validation
    const fetchEmployees = React.useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');

            const response = await axios.get(
                `${API_BASE}/get_employees.php`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                setEmployees(response.data.employees || []);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    }, [API_BASE]);

    useEffect(() => {
        if (show) {
            fetchDeductionComponents();
            fetchEmployees();
        }
    }, [show, fetchDeductionComponents, fetchEmployees]);

    const downloadTemplate = () => {
        if (selectedComponents.length === 0) {
            alert('Please select at least one deduction component first');
            return;
        }

        // Get employee codes
        const employeeCodes = employees
            .filter(emp => emp.status === 'Active')
            .map(emp => emp.emp_code);

        // Create template data structure (crosstab format)
        const headerRow = ['emp_code', ...selectedComponents.map(comp => comp.component_name)];

        // Create sample data with a few employees
        const sampleData = employeeCodes.slice(0, 5).map(empCode => {
            const row = { emp_code: empCode };
            selectedComponents.forEach(comp => {
                // Add sample values based on component type
                row[comp.component_name] = comp.calculation_type === 'Percentage' ? '10' : '1000';
            });
            return row;
        });

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(sampleData, { header: headerRow });

        // Set column widths
        const wscols = [
            { wch: 15 }, // emp_code
            ...selectedComponents.map(() => ({ wch: 20 })) // component columns
        ];
        ws['!cols'] = wscols;

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Salary Deductions");

        // Add instructions sheet
        const instructions = [
            ['SALARY DEDUCTIONS IMPORT TEMPLATE - INSTRUCTIONS'],
            [''],
            ['HOW TO FILL THIS TEMPLATE:'],
            ['1. Fill values in the appropriate columns for each employee'],
            ['2. Use only numeric values (no currency symbols)'],
            ['3. For percentages, enter numbers like 10, 15.5, etc. (without % sign)'],
            ['4. Leave cells blank if no deduction for that component'],
            [''],
            ['COLUMN DESCRIPTIONS:'],
            ['emp_code - Employee code (must match existing employee codes)'],
            ...selectedComponents.map(comp => [
                `${comp.component_name} - ${comp.component_type} (${comp.calculation_type})`
            ]),
            [''],
            ['VALUE FORMATS:'],
            ['• Fixed Amount: Enter numeric value (e.g., 1000, 1500.50)'],
            ['• Percentage: Enter percentage number (e.g., 10, 12.5, 15)'],
            ['• Formula: Leave blank or enter override value'],
            [''],
            ['IMPORTANT NOTES:'],
            ['1. Do not change column names'],
            ['2. Do not add or remove columns'],
            ['3. Ensure employee codes are correct'],
            ['4. Save file as CSV or Excel before importing'],
            ['5. Maximum file size: 5MB']
        ];

        const ws2 = XLSX.utils.aoa_to_sheet(instructions);
        ws2['!cols'] = [{ wch: 80 }];
        XLSX.utils.book_append_sheet(wb, ws2, "Instructions");

        // Add validation sheet
        const validation = [
            ['DATA VALIDATION'],
            [''],
            ['Field', 'Type', 'Required', 'Format'],
            ['emp_code', 'Text', 'Yes', 'Existing employee code'],
            ...selectedComponents.map(comp => [
                comp.component_name,
                comp.component_type,
                'No',
                comp.calculation_type === 'Percentage' ? 'Number (e.g., 10)' : 'Number (e.g., 1000)'
            ])
        ];

        const ws3 = XLSX.utils.aoa_to_sheet(validation);
        ws3['!cols'] = [
            { wch: 25 },
            { wch: 15 },
            { wch: 10 },
            { wch: 30 }
        ];
        XLSX.utils.book_append_sheet(wb, ws3, "Validation Rules");

        // Save file
        const fileName = `salary_deductions_template_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    const handleFileUpload = (event) => {
        if (!canImportSalary) {
            alert('You do not have permission to import salary details');
            return;
        }

        const file = event.target.files[0];
        if (!file) return;

        const fileName = file.name.toLowerCase();
        const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

        if (isExcel) {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                    const jsonData = XLSX.utils.sheet_to_json(firstSheet, { raw: false });

                    if (jsonData && jsonData.length > 0) {
                        setCsvData(jsonData);
                    } else {
                        setError('No valid data found in Excel file');
                    }
                } catch (error) {
                    setError('Error reading Excel file: ' + error.message);
                }
            };
            reader.readAsArrayBuffer(file);
        } else {
            Papa.parse(file, {
                header: true,
                complete: (results) => {
                    if (results.data && results.data.length > 0) {
                        setCsvData(results.data);
                    } else {
                        setError('No valid data found in CSV file');
                    }
                },
                error: (error) => {
                    setError('Error parsing CSV file: ' + error.message);
                }
            });
        }
    };

    const handleImport = async () => {
        if (!canImportSalary) {
            alert('You do not have permission to import salary details');
            return;
        }

        if (!csvData.length) {
            setError('No data to import');
            return;
        }

        setIsImporting(true);
        setImportProgress(0);
        setError('');

        try {
            await onImport(csvData, setImportProgress);
            onHide();
            setCsvData([]);
            setSelectedComponents([]);
        } catch (error) {
            setError('Import failed: ' + error.message);
        } finally {
            setIsImporting(false);
        }
    };

    const handleClose = () => {
        setCsvData([]);
        setImportProgress(0);
        setIsImporting(false);
        setSelectedComponents([]);
        onHide();
    };

    const toggleComponent = (component) => {
        setSelectedComponents(prev => {
            const exists = prev.find(c => c.component_id === component.component_id);
            if (exists) {
                return prev.filter(c => c.component_id !== component.component_id);
            } else {
                return [...prev, component];
            }
        });
    };

    if (!show) return null;

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content border-0 shadow">
                    <div className="modal-header bg-primary text-white">
                        <h5 className="modal-title">
                            <i className="bi bi-upload me-2"></i>
                            Import Salary Deductions
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={handleClose}
                            disabled={isImporting}
                        ></button>
                    </div>

                    <div className="modal-body">
                        <div className="row g-4">
                            {/* Component Selection */}
                            <div className="col-12">
                                <div className="card border-primary">
                                    <div className="card-header bg-primary text-white">
                                        <h6 className="mb-0">
                                            <i className="bi bi-list-check me-2"></i>
                                            Step 1: Select Deduction Components
                                        </h6>
                                    </div>
                                    <div className="card-body">
                                        {loadingComponents ? (
                                            <div className="text-center py-3">
                                                <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                                                Loading deduction components...
                                            </div>
                                        ) : deductionComponents.length === 0 ? (
                                            <div className="alert alert-warning">
                                                <i className="bi bi-exclamation-triangle me-2"></i>
                                                No deduction components found in salary details.
                                            </div>
                                        ) : (
                                            <>
                                                <div className="mb-3">
                                                    <small className="text-muted">
                                                        Select the deduction components you want to include in the import template.
                                                        Selected: {selectedComponents.length} of {deductionComponents.length}
                                                    </small>
                                                </div>
                                                <div className="row g-2">
                                                    {deductionComponents.map(component => (
                                                        <div key={component.component_id} className="col-md-4">
                                                            <div className="form-check card border">
                                                                <div className="card-body py-2">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        id={`comp-${component.component_id}`}
                                                                        checked={selectedComponents.some(c => c.component_id === component.component_id)}
                                                                        onChange={() => toggleComponent(component)}
                                                                    />
                                                                    <label className="form-check-label ms-2" htmlFor={`comp-${component.component_id}`}>
                                                                        <strong>{component.component_name}</strong>
                                                                        <br />
                                                                        <small className="text-muted">
                                                                            Type: <span className="badge bg-danger">{component.component_type}</span>
                                                                            {' '} | {' '}
                                                                            Calc: <span className="badge bg-info">{component.calculation_type}</span>
                                                                        </small>
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="mt-3 text-center">
                                                    <button
                                                        className="btn btn-outline-primary"
                                                        onClick={() => setSelectedComponents(deductionComponents)}
                                                        disabled={selectedComponents.length === deductionComponents.length}
                                                    >
                                                        <i className="bi bi-check-all me-2"></i>
                                                        Select All
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-secondary ms-2"
                                                        onClick={() => setSelectedComponents([])}
                                                        disabled={selectedComponents.length === 0}
                                                    >
                                                        <i className="bi bi-x-circle me-2"></i>
                                                        Clear All
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Template Download */}
                            {selectedComponents.length > 0 && (
                                <div className="col-12">
                                    <div className="card border-success">
                                        <div className="card-header bg-success text-white">
                                            <h6 className="mb-0">
                                                <i className="bi bi-download me-2"></i>
                                                Step 2: Download Template
                                            </h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="row align-items-center">
                                                <div className="col-md-8">
                                                    <h6 className="text-success">
                                                        <i className="bi bi-file-earmark-excel me-2"></i>
                                                        Crosstab Format Template
                                                    </h6>
                                                    <p className="mb-2">
                                                        Template will include columns for: <strong>emp_code</strong> and{' '}
                                                        {selectedComponents.map(comp => (
                                                            <span key={comp.component_id} className="badge bg-light text-dark me-1">
                                                                {comp.component_name}
                                                            </span>
                                                        ))}
                                                    </p>
                                                    <p className="mb-0 text-muted small">
                                                        <i className="bi bi-info-circle me-1"></i>
                                                        Each row represents one employee with deduction values in separate columns
                                                    </p>
                                                </div>
                                                <div className="col-md-4 text-end">
                                                    <button
                                                        className="btn btn-success btn-lg"
                                                        onClick={downloadTemplate}
                                                        disabled={selectedComponents.length === 0}
                                                    >
                                                        <i className="bi bi-file-earmark-excel me-2"></i>
                                                        Download Template
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* File Upload */}
                            {selectedComponents.length > 0 && (
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-header bg-light">
                                            <h6 className="mb-0">
                                                <i className="bi bi-upload me-2"></i>
                                                Step 3: Upload Filled Template
                                            </h6>
                                        </div>
                                        <div className="card-body">
                                            <label className="form-label">Select File *</label>
                                            <div className="input-group">
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    accept=".csv,.xlsx,.xls"
                                                    onChange={handleFileUpload}
                                                    disabled={isImporting}
                                                />
                                                <button
                                                    className="btn btn-outline-success"
                                                    type="button"
                                                    onClick={downloadTemplate}
                                                    title="Download Template"
                                                    disabled={isImporting}
                                                >
                                                    <i className="bi bi-download me-1"></i>
                                                    Template
                                                </button>
                                            </div>
                                            <div className="form-text">
                                                Supports CSV, XLSX, XLS files. Must match the downloaded template structure.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Data Preview */}
                            {csvData.length > 0 && (
                                <div className="col-12">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h6 className="card-title mb-0">
                                                    <i className="bi bi-table me-2"></i>
                                                    Preview ({csvData.length} records found)
                                                </h6>
                                                <span className="badge bg-primary">
                                                    Crosstab Format
                                                </span>
                                            </div>
                                            <div className="table-responsive" style={{ maxHeight: '250px' }}>
                                                <table className="table table-sm table-bordered">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            {Object.keys(csvData[0]).map(key => (
                                                                <th key={key} className="small">
                                                                    {key === 'emp_code' ? (
                                                                        <span className="text-danger">{key}*</span>
                                                                    ) : (
                                                                        key
                                                                    )}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {csvData.slice(0, 5).map((row, index) => (
                                                            <tr key={index}>
                                                                {Object.entries(row).map(([key, value], i) => (
                                                                    <td
                                                                        key={i}
                                                                        className="small"
                                                                        style={{
                                                                            color: key === 'emp_code' && !value ? 'red' : 'inherit'
                                                                        }}
                                                                    >
                                                                        {key === 'emp_code' && !value ? (
                                                                            <span className="text-danger">Required!</span>
                                                                        ) : (
                                                                            value || ''
                                                                        )}
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {csvData.length > 5 && (
                                                <div className="text-center small text-muted mt-2">
                                                    <i className="bi bi-arrow-down"></i>
                                                    ... and {csvData.length - 5} more employees
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Import Progress */}
                            {isImporting && (
                                <div className="col-12">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <h6 className="card-title">
                                                <i className="bi bi-arrow-repeat me-2"></i>
                                                Importing Salary Deductions...
                                            </h6>
                                            <div className="progress mb-2">
                                                <div
                                                    className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                                                    style={{ width: `${importProgress}%` }}
                                                >
                                                    {importProgress}%
                                                </div>
                                            </div>
                                            <small className="text-muted">
                                                Updating salary details for employees. Please wait...
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Error Display */}
                            {error && (
                                <div className="col-12">
                                    <div className="alert alert-danger">
                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                        {error}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleClose}
                            disabled={isImporting}
                        >
                            Cancel
                        </button>
                        {selectedComponents.length > 0 && (
                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={downloadTemplate}
                                disabled={isImporting}
                            >
                                <i className="bi bi-download me-2"></i>
                                Get Template
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleImport}
                            disabled={!csvData.length || isImporting || selectedComponents.length === 0}
                        >
                            {isImporting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    Importing...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-upload me-2"></i>
                                    Start Import
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImportSalaryDetails;